import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Shield, Plus, Trash2, Edit2, Save, X, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AgeRestrictionsPanel() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRule, setNewRule] = useState({ category_keyword: "", match_type: "contains", assigned_restriction: "21_plus", notes: "" });
  const [showAddRule, setShowAddRule] = useState(false);
  const [editingBiz, setEditingBiz] = useState(null);
  const [editRestriction, setEditRestriction] = useState("none");
  const [bulkIds, setBulkIds] = useState("");
  const [bulkRestriction, setBulkRestriction] = useState("21_plus");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/age-restrictions/summary`, { headers });
      const d = await res.json();
      setData(d);
      setRules(d.rules || []);
    } catch (e) {
      toast.error("Failed to load age restrictions data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOverride = async (businessId, oldRestriction) => {
    try {
      await fetch(`${API}/api/admin/age-restrictions/override/${businessId}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ age_restriction: editRestriction, old_restriction: oldRestriction }),
      });
      toast.success("Age restriction updated");
      setEditingBiz(null);
      fetchData();
    } catch { toast.error("Failed to update"); }
  };

  const handleBulkAssign = async () => {
    const ids = bulkIds.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
    if (!ids.length) return toast.error("Enter at least one business ID");
    try {
      const res = await fetch(`${API}/api/admin/age-restrictions/bulk-assign`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ business_ids: ids, age_restriction: bulkRestriction }),
      });
      const d = await res.json();
      toast.success(`Updated ${d.modified} businesses`);
      setBulkIds("");
      fetchData();
    } catch { toast.error("Bulk assign failed"); }
  };

  const handleAddRule = async () => {
    if (!newRule.category_keyword.trim()) return toast.error("Keyword required");
    try {
      const res = await fetch(`${API}/api/admin/age-restrictions/rules`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(newRule),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast.success("Rule added");
      setNewRule({ category_keyword: "", match_type: "contains", assigned_restriction: "21_plus", notes: "" });
      setShowAddRule(false);
      fetchData();
    } catch (e) { toast.error(e.message || "Failed to add rule"); }
  };

  const handleDeleteRule = async (keyword) => {
    if (!confirm(`Delete rule "${keyword}"?`)) return;
    try {
      await fetch(`${API}/api/admin/age-restrictions/rules/${encodeURIComponent(keyword)}`, {
        method: "DELETE", headers,
      });
      toast.success("Rule deleted");
      fetchData();
    } catch { toast.error("Failed to delete rule"); }
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading age restrictions...</div>;

  const counts = data?.counts || {};
  const noneCount = (counts["none"] || 0) + (counts["null"] || 0) + (counts[""] || 0);

  return (
    <div className="space-y-6" data-testid="age-restrictions-panel">
      {/* Counts */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-200">
          <p className="text-2xl font-bold text-slate-700" data-testid="count-none">{noneCount}</p>
          <p className="text-xs text-slate-500">No Restriction</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center border border-amber-200">
          <p className="text-2xl font-bold text-amber-600" data-testid="count-18-plus">{counts["18_plus"] || 0}</p>
          <p className="text-xs text-amber-600">18+ Only</p>
        </div>
        <div className="bg-red-50 rounded-lg p-3 text-center border border-red-200">
          <p className="text-2xl font-bold text-red-600" data-testid="count-21-plus">{counts["21_plus"] || 0}</p>
          <p className="text-xs text-red-600">21+ Only</p>
        </div>
      </div>

      {/* 21+ Businesses */}
      {data?.restricted_21_plus?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-red-600 mb-2">21+ Businesses ({data.restricted_21_plus.length})</h4>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {data.restricted_21_plus.map(b => (
              <div key={b.business_id} className="flex items-center justify-between bg-red-50/50 rounded px-3 py-1.5 text-sm">
                <div>
                  <span className="font-medium text-slate-700">{b.name}</span>
                  <span className="text-slate-400 ml-2 text-xs">{b.category} · {b.city}</span>
                </div>
                {editingBiz === b.business_id ? (
                  <div className="flex items-center gap-1">
                    <select value={editRestriction} onChange={e => setEditRestriction(e.target.value)} className="text-xs border rounded px-1 py-0.5">
                      <option value="none">None</option>
                      <option value="18_plus">18+</option>
                      <option value="21_plus">21+</option>
                    </select>
                    <button onClick={() => handleOverride(b.business_id, b.age_restriction)} className="text-emerald-600 hover:text-emerald-800"><Save size={14} /></button>
                    <button onClick={() => setEditingBiz(null)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                  </div>
                ) : (
                  <button onClick={() => { setEditingBiz(b.business_id); setEditRestriction(b.age_restriction); }} className="text-slate-400 hover:text-slate-600" data-testid={`edit-${b.business_id}`}><Edit2 size={14} /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 18+ Businesses */}
      {data?.restricted_18_plus?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-amber-600 mb-2">18+ Businesses ({data.restricted_18_plus.length})</h4>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {data.restricted_18_plus.map(b => (
              <div key={b.business_id} className="flex items-center justify-between bg-amber-50/50 rounded px-3 py-1.5 text-sm">
                <div>
                  <span className="font-medium text-slate-700">{b.name}</span>
                  <span className="text-slate-400 ml-2 text-xs">{b.category} · {b.city}</span>
                </div>
                {editingBiz === b.business_id ? (
                  <div className="flex items-center gap-1">
                    <select value={editRestriction} onChange={e => setEditRestriction(e.target.value)} className="text-xs border rounded px-1 py-0.5">
                      <option value="none">None</option>
                      <option value="18_plus">18+</option>
                      <option value="21_plus">21+</option>
                    </select>
                    <button onClick={() => handleOverride(b.business_id, b.age_restriction)} className="text-emerald-600 hover:text-emerald-800"><Save size={14} /></button>
                    <button onClick={() => setEditingBiz(null)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                  </div>
                ) : (
                  <button onClick={() => { setEditingBiz(b.business_id); setEditRestriction(b.age_restriction); }} className="text-slate-400 hover:text-slate-600"><Edit2 size={14} /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Auto-Detection Log */}
      {data?.auto_detection_log?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-600 mb-2">Auto-Detection Log (Recent {data.auto_detection_log.length})</h4>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {data.auto_detection_log.map((log, i) => (
              <div key={i} className="flex items-center justify-between bg-slate-50 rounded px-3 py-1.5 text-xs">
                <span className="text-slate-600">{log.business_id}</span>
                <span className={log.action === "manual_override" ? "text-violet-600" : "text-emerald-600"}>
                  {log.action} → {log.new_restriction}
                </span>
                <span className="text-slate-400">{log.detected_at?.slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bulk Assignment */}
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
        <h4 className="text-sm font-semibold text-slate-600 mb-2">Bulk Assignment</h4>
        <textarea
          value={bulkIds}
          onChange={e => setBulkIds(e.target.value)}
          placeholder="Enter business IDs (comma or newline separated)"
          className="w-full text-xs border rounded p-2 h-16 resize-none mb-2"
          data-testid="bulk-ids-input"
        />
        <div className="flex items-center gap-2">
          <select value={bulkRestriction} onChange={e => setBulkRestriction(e.target.value)} className="text-xs border rounded px-2 py-1" data-testid="bulk-restriction-select">
            <option value="none">None</option>
            <option value="18_plus">18+</option>
            <option value="21_plus">21+</option>
          </select>
          <button onClick={handleBulkAssign} className="text-xs bg-slate-700 text-white rounded px-3 py-1 hover:bg-slate-800" data-testid="bulk-assign-btn">Assign</button>
        </div>
      </div>

      {/* Rules CRUD */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-slate-600">Detection Rules ({rules.length})</h4>
          <button onClick={() => setShowAddRule(!showAddRule)} className="text-xs flex items-center gap-1 text-emerald-600 hover:text-emerald-800" data-testid="add-rule-btn">
            <Plus size={14} /> Add Rule
          </button>
        </div>

        {showAddRule && (
          <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200 mb-3 space-y-2" data-testid="add-rule-form">
            <input
              value={newRule.category_keyword}
              onChange={e => setNewRule(r => ({ ...r, category_keyword: e.target.value }))}
              placeholder="Category keyword (e.g. cannabis)"
              className="w-full text-xs border rounded px-2 py-1"
              data-testid="new-rule-keyword"
            />
            <div className="flex gap-2">
              <select value={newRule.match_type} onChange={e => setNewRule(r => ({ ...r, match_type: e.target.value }))} className="text-xs border rounded px-2 py-1" data-testid="new-rule-match-type">
                <option value="contains">Contains</option>
                <option value="exact">Exact</option>
              </select>
              <select value={newRule.assigned_restriction} onChange={e => setNewRule(r => ({ ...r, assigned_restriction: e.target.value }))} className="text-xs border rounded px-2 py-1" data-testid="new-rule-restriction">
                <option value="18_plus">18+</option>
                <option value="21_plus">21+</option>
              </select>
            </div>
            <input
              value={newRule.notes}
              onChange={e => setNewRule(r => ({ ...r, notes: e.target.value }))}
              placeholder="Notes (optional)"
              className="w-full text-xs border rounded px-2 py-1"
            />
            <div className="flex gap-2">
              <button onClick={handleAddRule} className="text-xs bg-emerald-600 text-white rounded px-3 py-1 hover:bg-emerald-700" data-testid="save-new-rule-btn">Save</button>
              <button onClick={() => setShowAddRule(false)} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
            </div>
          </div>
        )}

        <div className="space-y-1">
          {rules.map(rule => (
            <div key={rule.category_keyword} className="flex items-center justify-between bg-white rounded px-3 py-2 text-sm border border-slate-100">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${rule.assigned_restriction === "21_plus" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-600"}`}>
                  {rule.assigned_restriction === "21_plus" ? "21+" : "18+"}
                </span>
                <span className="font-medium text-slate-700">{rule.category_keyword}</span>
                <span className="text-xs text-slate-400">({rule.match_type})</span>
              </div>
              <button onClick={() => handleDeleteRule(rule.category_keyword)} className="text-slate-300 hover:text-red-500" data-testid={`delete-rule-${rule.category_keyword}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <button onClick={fetchData} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 mx-auto" data-testid="refresh-age-restrictions">
        <RefreshCw size={12} /> Refresh
      </button>
    </div>
  );
}
